import { ethers, type Signer } from "ethers";
import { type AxiosInstance, type AxiosResponse } from "axios";
import type {
  CheckAddressResponse,
  Eip1193Provider,
  EthereumProvider,
  LoginResponse,
} from "../types/helpers.types";

export const isTrustWallet = (): boolean => {
  if (!window?.ethereum) {
    return false;
  }
  const { ethereum } = window;
  if (ethereum?.isTrust || ethereum?.isTrustWallet) {
    return true;
  }
  if (ethereum?.providers && Array.isArray(ethereum.providers)) {
    return ethereum.providers.some(
      (provider: EthereumProvider) => provider.isTrust || provider.isTrustWallet
    );
  }
  return false;
};

export const normalizeChainId = (chainId: string | number): number => {
  if (typeof chainId === "number") {
    return chainId;
  }
  if (typeof chainId === "string") {
    return chainId.startsWith("0x")
      ? parseInt(chainId, 16)
      : parseInt(chainId, 10);
  }
  return 1;
};

export const getValidatedChainId = async (
  provider: Eip1193Provider | ethers.Provider,
  fallbackChainId: number = 1
): Promise<number> => {
  let chainId: number = fallbackChainId;
  const isSendProvider = (p: any): p is { send: Eip1193Provider["send"] } =>
    typeof p.send === "function";

  if (isSendProvider(provider) && provider.send) {
    try {
      const networkChainId = await provider.send("eth_chainId", []);
      chainId = normalizeChainId(networkChainId);
      console.log("ChainId retrieved via eth_chainId:", chainId);
      return chainId;
    } catch (error) {
      console.warn("Failed to get chainId via eth_chainId:", error);
    }
    try {
      const networkVersion = await provider.send("net_version", []);
      chainId = normalizeChainId(networkVersion);
      console.log("ChainId retrieved via net_version:", chainId);
      return chainId;
    } catch (error) {
      console.warn("Failed to get chainId via net_version:", error);
    }
  }
  if (
    window?.ethereum &&
    "chainId" in window.ethereum &&
    window.ethereum.chainId
  ) {
    try {
      chainId = normalizeChainId(window.ethereum.chainId as string);
      console.log("ChainId retrieved via window.ethereum.chainId:", chainId);
      return chainId;
    } catch (error) {
      console.warn("Failed to parse window.ethereum.chainId:", error);
    }
  }

  if (
    window?.ethereum &&
    "request" in window.ethereum &&
    typeof window.ethereum.request === "function"
  ) {
    try {
      const requestedChainId = await window.ethereum.request({
        method: "eth_chainId",
      });
      chainId = normalizeChainId(requestedChainId);
      console.log("ChainId retrieved via ethereum.request:", chainId);
      return chainId;
    } catch (error) {
      console.warn("Failed to get chainId via ethereum.request:", error);
    }
  }
  console.warn(
    "All chainId retrieval methods failed, using fallback:",
    fallbackChainId
  );
  return fallbackChainId;
};

export const handleTrustWalletChainIdMismatch = async (
  provider: Eip1193Provider | ethers.Provider,
  address: string,
  axios: AxiosInstance,
  signer: Signer
): Promise<{ response: AxiosResponse<LoginResponse>; chainId: number }> => {
  console.log("Handling Trust Wallet chainId mismatch...");
  try {
    const actualChainId = await getValidatedChainId(provider);
    console.log("Retrying with validated chainId:", actualChainId);
    const currentOrigin = window.location.origin;
    const checkAddressParams = {
      address,
      chainId: actualChainId,
      domain: currentOrigin,
    };
    console.log(
      "Trust Wallet retry - sending check-address request with params:",
      checkAddressParams
    );
    const checkAddressResponse: AxiosResponse<CheckAddressResponse> =
      await axios.get("/api/auth/check-address", {
        params: checkAddressParams,
      });
    const { message } = checkAddressResponse.data;
    const signature = await signer.signMessage(message);
    const response: AxiosResponse<LoginResponse> = await axios.post(
      "/api/auth/login",
      {
        message,
        signature,
        chainId: actualChainId,
        from: "rewards",
      }
    );

    return { response, chainId: actualChainId };
  } catch (error) {
    console.error("Trust Wallet chainId mismatch handling failed:", error);
    throw error;
  }
};
